import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import * as Yup from 'yup';

import { isFieldError } from '../../../../../helpers/formik';
import { useClassHelper } from '../../../../../hooks/useClassHelper';
import { IServices } from '../../../../../types';
import { DropdownNativeSelect } from '../../../../Dropdown/components/DropdownNativeSelect';
import { Form } from '../../../../Form/Form';
import { InputField } from '../../../../InputField/InputField';
import { Stack } from '../../../../Stack/Stack';
import { useStepWizard } from '../../../../StepWizard/hooks/useStepWizard';
import { TextEditor } from '../../../../TextEditor/TextEditor';
import { DATADOG_ACTIONS } from '../../../constants';
import { isTextChildrenEmpty } from '../../../helpers';
import { useDataDogActions } from '../../../hooks/useDataDogActions';
import { CA_PROVINCES, componentName, USA_STATES } from '../constants';
import { MappedPlace } from '../helpers/mapPlaceDetails';
import { normalizeValidation } from '../helpers/mapValidationResult';
import { useAddressErrorState } from '../hooks/useAddressErrorState';
import { useFormState } from '../hooks/useFormState';
import { useResetScrollOnMount } from '../hooks/useResetScrollOnMount';
import { useVoWifiRenderTracking } from '../hooks/useVoWifiRenderTracking';
import { AddressFormValues, PageProps, VoWifiAddress } from '../types';
import { AddressAutocomplete } from './AddressAutocomplete';
import { AddressHelpBox } from './AddressHelpBox';
import { Button } from './shared/Button';
import { HeaderImage } from './shared/HeaderImage';
import { Paragraph } from './shared/Paragraph';
import { Title } from './shared/Title';
import { SuggestedAddress } from './SuggestedAddress';

interface Props extends PageProps, Partial<Omit<VoWifiAddress, 'type'>> {
  // From GET /branding configuration_settings.emergency_address_autocomplete_enabled.
  autocompleteEnabled?: boolean;
  services?: Pick<
    IServices,
    'placesAutocomplete' | 'placeDetails' | 'validateAddress'
  >;
}

const defaultTexts = {
  title: 'Your Emergency 911 Address',
  subtitle: 'Input the address for 911 calls to be dispatched to.',
  content: [
    {
      type: 'paragraph',
      children: [
        {
          text: "It's critical to always have an up-to-date 9-1-1 Emergency Address because Emergency services cannot detect your physical location when you make a call over Wi-Fi.",
        },
      ],
    },
  ],
  submit: 'Submit',
  findAddress: 'Find Your Address',
  findAddressPlaceholder: 'Start typing your address',
  noResults: 'No addresses found',
  streetNumber: 'Street Number',
  streetName: 'Street Name',
  addressLine1: 'Address Line 1',
  addressHelp: 'Address Help',
} as const;

const isUS = (carrierCountry: string): boolean => {
  return carrierCountry?.toLowerCase?.() === 'us';
};

// Portal-preview support: selecting any autocomplete-only copy field forces
// the autocomplete face of the form, so the edited copy is always visible —
// even while the brand's flag is still off.
const AUTOCOMPLETE_PREVIEW_FIELDS: string[] = [
  'find_address_label',
  'find_address_placeholder',
  'no_results_label',
  'street_number_label',
  'street_number_placeholder',
  'psap_error_title',
  'psap_submit_label',
];

// Portal-preview support for the PSAP feedback copy: selecting any of these
// renders the error face (help box + error title + "Update Address" submit)
// with a fixture carrier message, since no real 422 can occur in the preview.
const PSAP_PREVIEW_FIELDS: string[] = ['psap_error_title', 'psap_submit_label'];
const PREVIEW_PSAP_DETAILS =
  'That postal code does not match the given city (Halifax, Nova Scotia). Did you mean Dartmouth, Nova Scotia?';

// Portal-preview support for the "Suggested Address Found" copy fields:
// selecting any of them renders the suggestion face with these fixtures.
const SUGGESTION_FIELDS: string[] = [
  'suggestions_title',
  'suggestion_instruction',
  'you_entered_label',
  'suggested_address_label',
  'suggestion_info_label',
  'use_selected_address',
  'use_address_as_entered',
  'suggestion_edit_address',
];

// Portal-preview support for the "Unverified Address" copy fields: selecting
// any of them renders the unverified face with the fixture entered address.
const UNVERIFIED_FIELDS: string[] = [
  'unverified_title',
  'unverified_instruction',
  'use_address_anyway',
];

// Address-validation sub-state: which result screen replaces the form after
// a submit. 'corrected' shows Google's suggestion; 'unconfirmed' shows the
// entered address with a "use anyway" escape hatch.
type ValidationSubState =
  | {
      kind: 'corrected';
      suggested: AddressFormValues;
      userValues: AddressFormValues;
    }
  | { kind: 'unconfirmed'; userValues: AddressFormValues };

const PREVIEW_SUGGESTION = {
  userValues: {
    street_number: '123',
    address_line_1: 'Fake Street',
    address_line_2: '7',
    city: 'Dartmoth',
    state_province_region: 'NS',
    zip_postal_code: '',
  },
  suggested: {
    street_number: '123',
    address_line_1: 'Fake Street',
    address_line_2: '7',
    city: 'Dartmouth',
    state_province_region: 'NS',
    zip_postal_code: 'B2Y 1A1',
  },
} as const;

// Stable references: a fresh object here would retrigger the scroll-reset
// effect keyed on the active sub-state on every preview render.
const PREVIEW_CORRECTED: ValidationSubState = {
  kind: 'corrected',
  ...PREVIEW_SUGGESTION,
};
const PREVIEW_UNVERIFIED: ValidationSubState = {
  kind: 'unconfirmed',
  userValues: PREVIEW_SUGGESTION.userValues,
};

const resolveText = (
  override: string | undefined | null,
  fallback: string,
): string => {
  return override && override.trim().length > 0 ? override : fallback;
};

export const AddressScreen = ({
  themeValues,
  carrierCountry,
  isPortalPreview,
  autocompleteEnabled: autocompleteEnabledProp = false,
  services,
  title,
  submit,
  subtitle,
  content_address,
  address_1_label,
  address_2_label,
  state_label,
  state_placeholder,
  city_label,
  zip_label,
  is_required_label,
  address_1_placeholder,
  address_2_placeholder,
  city_placeholder,
  zip_placeholder,
  find_address_label,
  find_address_placeholder,
  no_results_label,
  street_number_label,
  street_number_placeholder,
  psap_error_title,
  psap_submit_label,
  suggestions_title,
  suggestion_instruction,
  you_entered_label,
  suggested_address_label,
  suggestion_info_label,
  use_selected_address,
  use_address_as_entered,
  suggestion_edit_address,
  unverified_title,
  unverified_instruction,
  use_address_anyway,
  selectedField,
}: Props) => {
  const { actions, dataDogLogger } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const autocompleteEnabled =
    autocompleteEnabledProp ||
    (isPortalPreview &&
      AUTOCOMPLETE_PREVIEW_FIELDS.includes(selectedField?.field ?? ''));

  const { containerRef, onImageError } = useVoWifiRenderTracking(
    isPortalPreview,
    'address',
  );

  // Reset scroll on mount
  useResetScrollOnMount();

  const c = useClassHelper(componentName);
  const { nextStep } = useStepWizard();
  const { formState, setFormValues } = useFormState();
  const { addressErrorDetails, reset: resetAddressError } =
    useAddressErrorState();

  // Address-validation sub-state (Google Address Validation). Local: it only
  // exists between a submit and the user's accept/use-anyway/edit choice.
  const [suggestion, setSuggestion] = useState<ValidationSubState | null>(null);
  // True while a picked place resolves through Place Details: the manual
  // fields lock so the (late) response can't overwrite fresh typing.
  const [resolvingPlace, setResolvingPlace] = useState(false);

  const countryIsUS = isUS(carrierCountry);
  const regionCode = countryIsUS ? 'US' : 'CA';
  const stateProvinceRegionList = countryIsUS ? USA_STATES : CA_PROVINCES;

  const stateProvinceRegionLabel = resolveText(
    state_label,
    countryIsUS ? 'State' : 'Province',
  );

  const getStatePlaceholderDefault = (): string => {
    // New (autocomplete) flow follows Figma 2454-845 copy; legacy unchanged.
    if (!autocompleteEnabled) {
      return 'Select here';
    }
    return countryIsUS ? 'Select state' : 'Select province';
  };

  const stateProvinceRegionPlaceholder = resolveText(
    state_placeholder,
    getStatePlaceholderDefault(),
  );

  const zipPostalCodeLabel = resolveText(
    zip_label,
    countryIsUS ? 'Zip Code' : 'Postal Code',
  );

  const requiredLabel = resolveText(is_required_label, 'is Required');

  // With autocomplete on, address_line_1 becomes the street NAME (the civic
  // number has its own field); otherwise the legacy single-line label stays.
  const address1Label = resolveText(
    address_1_label,
    autocompleteEnabled ? defaultTexts.streetName : defaultTexts.addressLine1,
  );
  const streetNumberLabel = resolveText(
    street_number_label,
    defaultTexts.streetNumber,
  );
  const cityLabel = resolveText(city_label, 'City');
  const address2Label = resolveText(
    address_2_label,
    autocompleteEnabled ? 'Apt/Suite' : 'Address Line 2 (Optional)',
  );

  // PSAP feedback mode (Figma 2481-970): the form re-renders under an error
  // title with the alert on top and an "Update Address" submit. The portal
  // preview forces it (with a fixture message) while a PSAP copy field is
  // selected, so the edited copy is visible.
  const previewingPsap =
    isPortalPreview && PSAP_PREVIEW_FIELDS.includes(selectedField?.field ?? '');
  const activeErrorDetails = previewingPsap
    ? PREVIEW_PSAP_DETAILS
    : addressErrorDetails;
  const isPsapMode = autocompleteEnabled && Boolean(activeErrorDetails);

  // PSAP copy has its own keys: the brand's regular title/submit must not
  // leak into the error face (and vice versa).
  const screenTitle = isPsapMode
    ? resolveText(psap_error_title, 'An Error Occurred')
    : resolveText(title, defaultTexts.title);
  const submitLabel = isPsapMode
    ? resolveText(psap_submit_label, 'Update Address')
    : resolveText(
        submit,
        autocompleteEnabled ? 'Confirm' : defaultTexts.submit,
      );

  const address1Placeholder = resolveText(address_1_placeholder, '');
  const address2Placeholder = resolveText(address_2_placeholder, '');
  const cityPlaceholder = resolveText(city_placeholder, '');
  const zipPlaceholder = resolveText(zip_placeholder, '');
  const streetNumberPlaceholder = resolveText(street_number_placeholder, '');

  const validationSchema = {
    // Optional on the carrier API; required here only when the brand enabled
    // autocomplete (UES-11807 — requiredness is enforced FE-side off the flag).
    street_number: autocompleteEnabled
      ? Yup.string().trim().required(`${streetNumberLabel} ${requiredLabel}`)
      : Yup.string(),
    address_line_1: Yup.string().required(`${address1Label} ${requiredLabel}`),
    address_line_2: Yup.string(),
    city: Yup.string().required(`${cityLabel} ${requiredLabel}`),
    state_province_region: Yup.string().required(
      `${stateProvinceRegionLabel} ${requiredLabel}`,
    ),
    zip_postal_code: Yup.string().required(
      `${zipPostalCodeLabel} ${requiredLabel}`,
    ),
  };

  const formik = useFormik<AddressFormValues>({
    initialValues: formState,
    // New flow: the submit button stays disabled until the form is valid
    // (Figma 2454-845) — mount-time validation keeps `isValid` accurate
    // without painting untouched fields with errors.
    validateOnMount: autocompleteEnabled,
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit: async (values, { setSubmitting }) => {
      if (isPortalPreview) {
        return;
      }

      const proceed = () => {
        setFormValues(values);
        // A fresh submission supersedes the previous PSAP feedback.
        resetAddressError();
        nextStep();
      };

      if (!autocompleteEnabled || !services?.validateAddress) {
        setSubmitting(false);
        proceed();
        return;
      }

      // Google Address Validation — one call per submit. Fail-open: any
      // API problem proceeds to Confirmation; the carrier stays the final
      // authority on the address.
      try {
        const streetLine = [values.street_number, values.address_line_1]
          .filter(Boolean)
          .join(' ');
        const result = await services.validateAddress({
          regionCode,
          addressLines: [streetLine, values.address_line_2].filter(Boolean),
          locality: values.city,
          administrativeArea: values.state_province_region,
          postalCode: values.zip_postal_code,
        });

        const normalized = normalizeValidation(values, result);

        if (normalized.kind === 'corrected') {
          dataDogLogger.log(actions?.address_validation_suggested, {
            context: 'vowifi',
            changedFields: normalized.changedFields,
          });
          setSuggestion({
            kind: 'corrected',
            suggested: normalized.suggested,
            userValues: values,
          });
          return;
        }

        if (normalized.kind === 'unconfirmed') {
          dataDogLogger.log(actions?.address_validation_unconfirmed, {
            context: 'vowifi',
          });
          setSuggestion({ kind: 'unconfirmed', userValues: values });
          return;
        }

        proceed();
      } catch (error) {
        dataDogLogger.warning(DATADOG_ACTIONS.errors.template_error, {
          flow: 'address_validation',
          errorCode: 'ADDRESS_VALIDATION_API_ERROR',
          errorMessage: (error as Error)?.message,
        });
        proceed();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    isSubmitting,
    handleSubmit,
    values,
    handleChange,
    touched,
    errors,
    handleBlur,
  } = formik;

  const fieldsLocked = isSubmitting || resolvingPlace;

  const isEditingRequiredLabel =
    selectedField?.field === 'is_required_label' && isPortalPreview;

  useUpdateEffect(() => {
    if (isEditingRequiredLabel) {
      formik.setTouched({
        ...(autocompleteEnabled ? { street_number: true } : {}),
        address_line_1: true,
        city: true,
        state_province_region: true,
        zip_postal_code: true,
      });
      formik.validateForm();
    } else {
      formik.setTouched({});
    }
  }, [selectedField]);

  useUpdateEffect(() => {
    if (isEditingRequiredLabel) {
      formik.validateForm();
    }
  }, [requiredLabel]);

  const onBlurEvent = (
    event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (isPortalPreview) {
      return;
    }

    // Call the original handleBlur to ensure Formik's validation and touched state updates work correctly
    handleBlur(event);
  };

  const handleSubmitEvent = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isPortalPreview) {
      return;
    }

    // Call the original handleSubmit to ensure Formik's onSubmit logic is executed
    handleSubmit(event);
  };

  const handlePlaceSelected = useCallback(
    (place: MappedPlace) => {
      const { country, ...address } = place;

      if (country && country.toUpperCase() !== regionCode) {
        // Payload always carries the brand country; log the mismatch only.
        dataDogLogger.warning(DATADOG_ACTIONS.errors.template_error, {
          flow: 'places_autocomplete',
          errorCode: 'PLACE_COUNTRY_MISMATCH',
          errorMessage: `Selected place country ${country} differs from brand ${regionCode}`,
        });
      }

      formik.setValues({ ...formik.values, ...address });
      // shouldValidate:false — setTouched's own validation pass runs against
      // the pre-pick (empty) values and briefly paints every filled field
      // with an "is Required" error; setValues already validates against the
      // new values.
      formik.setTouched(
        Object.keys(address).reduce((acc, key) => {
          return { ...acc, [key]: true };
        }, {} as Record<string, boolean>),
        false,
      );
      dataDogLogger.log(actions?.address_place_selected, {
        context: 'vowifi',
        hasStreetNumber: Boolean(address.street_number),
      });
    },
    [formik, regionCode, dataDogLogger, actions],
  );

  const handlePlacesError = useCallback(
    (flow: string) => {
      return (error: Error) => {
        dataDogLogger.warning(DATADOG_ACTIONS.errors.template_error, {
          flow,
          errorCode: 'PLACES_API_ERROR',
          errorMessage: error?.message,
        });
      };
    },
    [dataDogLogger],
  );

  // Stable handler identities: AddressAutocomplete re-reports its error
  // whenever the callback prop changes, and this component re-renders on
  // every keystroke of the manual fields.
  const handleSearchError = useMemo(() => {
    return handlePlacesError('places_autocomplete');
  }, [handlePlacesError]);
  const handleResolveError = useMemo(() => {
    return handlePlacesError('places_details');
  }, [handlePlacesError]);

  // Production build without a Places key: the websheet ships no Places
  // services at all (never the mock fixtures), so the form degrades to manual
  // entry. Surface that once — a brand paid for autocomplete and isn't
  // getting it.
  const placesUnavailable =
    autocompleteEnabled && !isPortalPreview && !services?.placesAutocomplete;
  const placesUnavailableReported = useRef(false);

  useEffect(() => {
    if (!placesUnavailable || placesUnavailableReported.current) {
      return;
    }
    placesUnavailableReported.current = true;
    dataDogLogger.warning(DATADOG_ACTIONS.errors.template_error, {
      flow: 'places_autocomplete',
      errorCode: 'PLACES_UNAVAILABLE',
      errorMessage:
        'Places services are not configured — address autocomplete and validation disabled',
    });
  }, [placesUnavailable, dataDogLogger]);

  const renderContentAddress = () => {
    const isContentAddressEmpty = isTextChildrenEmpty(content_address);

    // Early return, when the content its empty, so it will looks better!
    if (isContentAddressEmpty) {
      return null;
    }

    return (
      <TextEditor
        viewOnly
        value={content_address ? content_address : defaultTexts.content}
        valueFontParagraph={themeValues.fontParagraph}
      />
    );
  };

  const renderAddressHelp = () => {
    if (!activeErrorDetails) {
      return null;
    }

    return (
      <AddressHelpBox
        title={defaultTexts.addressHelp}
        details={activeErrorDetails}
        themeValues={themeValues}
      />
    );
  };

  const renderAutocomplete = () => {
    if (
      !autocompleteEnabled ||
      !services?.placesAutocomplete ||
      !services.placeDetails
    ) {
      return null;
    }

    const placesServices = {
      placesAutocomplete: services.placesAutocomplete,
      placeDetails: services.placeDetails,
    };

    return (
      <AddressAutocomplete
        id="find_address"
        label={resolveText(find_address_label, defaultTexts.findAddress)}
        required
        placeholder={resolveText(
          find_address_placeholder,
          defaultTexts.findAddressPlaceholder,
        )}
        noResultsLabel={resolveText(no_results_label, defaultTexts.noResults)}
        themeValues={themeValues}
        services={placesServices}
        regionCode={regionCode}
        enabled={autocompleteEnabled}
        disabled={isSubmitting}
        isPortalPreview={isPortalPreview}
        onSelect={handlePlaceSelected}
        onSearchError={handleSearchError}
        onResolveError={handleResolveError}
        onResolvingChange={setResolvingPlace}
      />
    );
  };

  // Portal preview: selecting any suggestion/unverified copy field
  // live-previews that face with fixture addresses (same pattern as
  // is_required_label above).
  const previewingSuggestion =
    isPortalPreview && SUGGESTION_FIELDS.includes(selectedField?.field ?? '');
  const previewingUnverified =
    isPortalPreview && UNVERIFIED_FIELDS.includes(selectedField?.field ?? '');

  const getActiveSuggestion = (): ValidationSubState | null => {
    if (previewingSuggestion) {
      return PREVIEW_CORRECTED;
    }
    if (previewingUnverified) {
      return PREVIEW_UNVERIFIED;
    }
    return suggestion;
  };

  const activeSuggestion = getActiveSuggestion();

  // Every accept path does the same four things — the address it commits and
  // the funnel event it emits are the only differences.
  const commitAndAdvance = (values: AddressFormValues, actionName?: string) => {
    dataDogLogger.log(actionName, { context: 'vowifi' });
    setFormValues(values);
    resetAddressError();
    setSuggestion(null);
    nextStep();
  };

  const handleUseSelected = () => {
    if (suggestion?.kind !== 'corrected') {
      return;
    }

    commitAndAdvance(
      suggestion.suggested,
      actions?.suggestion_use_selected_clicked,
    );
  };

  // Commits the same typed values as `handleUseAnyway`, but from the corrected
  // face — kept separate so each face keeps its own funnel event.
  const handleUseAsEntered = () => {
    if (suggestion?.kind !== 'corrected') {
      return;
    }

    commitAndAdvance(
      suggestion.userValues,
      actions?.suggestion_use_as_entered_clicked,
    );
  };

  const handleUseAnyway = () => {
    if (suggestion?.kind !== 'unconfirmed') {
      return;
    }

    commitAndAdvance(
      suggestion.userValues,
      actions?.unverified_use_anyway_clicked,
    );
  };

  const handleEditSuggested = () => {
    dataDogLogger.log(
      suggestion?.kind === 'unconfirmed'
        ? actions?.unverified_edit_address_clicked
        : actions?.suggestion_edit_address_clicked,
      {
        context: 'vowifi',
      },
    );
    // Formik still holds the user's typed values — just flip back to the form.
    setSuggestion(null);
  };

  // The sub-state switch replaces the whole screen; mirror the mount-time
  // scroll reset so the suggestion (and the form on return) starts at the top.
  // Skipped in the portal preview: the template renders inline in the portal
  // page, so `window` is the PORTAL window — focusing a validation copy field
  // flips this sub-state and would yank the editor's page (and the input they
  // just focused) to the top.
  useEffect(() => {
    if (isPortalPreview) {
      return;
    }
    window.scrollTo(0, 0);
  }, [activeSuggestion, isPortalPreview]);

  if (activeSuggestion) {
    const sharedSuggestionProps = {
      themeValues,
      userValues: activeSuggestion.userValues,
      isPortalPreview,
      onEdit: handleEditSuggested,
      you_entered_label,
      suggestion_edit_address,
    };

    return (
      <div ref={containerRef} className={c('__address-screen')}>
        <Stack gap={6} direction="column" justify="start">
          <HeaderImage
            src={themeValues.logoUrl}
            isPortalPreview={isPortalPreview}
            onError={() => onImageError(themeValues.logoUrl)}
          />
          {activeSuggestion.kind === 'unconfirmed' ? (
            <SuggestedAddress
              {...sharedSuggestionProps}
              variant="unverified"
              onUseSelected={handleUseAnyway}
              unverified_title={unverified_title}
              unverified_instruction={unverified_instruction}
              use_address_anyway={use_address_anyway}
            />
          ) : (
            <SuggestedAddress
              {...sharedSuggestionProps}
              suggested={activeSuggestion.suggested}
              onUseSelected={handleUseSelected}
              onUseAsEntered={handleUseAsEntered}
              suggestions_title={suggestions_title}
              suggestion_instruction={suggestion_instruction}
              suggested_address_label={suggested_address_label}
              suggestion_info_label={suggestion_info_label}
              use_selected_address={use_selected_address}
              use_address_as_entered={use_address_as_entered}
            />
          )}
        </Stack>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={c('__address-screen')}>
      <Stack gap={6} direction="column" justify="start">
        <HeaderImage
          src={themeValues.logoUrl}
          isPortalPreview={isPortalPreview}
          onError={() => onImageError(themeValues.logoUrl)}
        />
        <Title fontFamily={themeValues.fontTitle}>{screenTitle}</Title>

        {!isPsapMode && (
          <Paragraph fontFamily={themeValues.fontParagraph}>
            {resolveText(subtitle, defaultTexts.subtitle)}
          </Paragraph>
        )}

        {!isPsapMode && renderContentAddress()}

        {renderAddressHelp()}

        <Form onSubmit={handleSubmitEvent} loading={isSubmitting}>
          <Stack gap={5} direction="column">
            {renderAutocomplete()}

            {autocompleteEnabled && (
              <InputField
                app="websheet"
                type="text"
                label={streetNumberLabel}
                required={autocompleteEnabled}
                placeholder={streetNumberPlaceholder}
                id="street_number"
                name="street_number"
                value={values.street_number}
                onChange={handleChange}
                autoComplete="off"
                onBlur={onBlurEvent}
                error={isFieldError('street_number', touched, errors)}
                errorMessage={errors.street_number}
                disabled={fieldsLocked}
                aria-disabled={fieldsLocked}
              />
            )}

            <InputField
              app="websheet"
              type="text"
              label={address1Label}
              required={autocompleteEnabled}
              placeholder={address1Placeholder}
              id="address_line_1"
              name="address_line_1"
              value={values.address_line_1}
              onChange={handleChange}
              autoComplete="off"
              onBlur={onBlurEvent}
              error={isFieldError('address_line_1', touched, errors)}
              errorMessage={errors.address_line_1}
              disabled={fieldsLocked}
              aria-disabled={fieldsLocked}
            />

            <InputField
              app="websheet"
              type="text"
              label={address2Label}
              placeholder={address2Placeholder}
              id="address_line_2"
              name="address_line_2"
              value={values.address_line_2}
              onChange={handleChange}
              onBlur={onBlurEvent}
              autoComplete="off"
              error={isFieldError('address_line_2', touched, errors)}
              errorMessage={errors.address_line_2}
              disabled={fieldsLocked}
              aria-disabled={fieldsLocked}
            />

            <InputField
              app="websheet"
              type="text"
              label={cityLabel}
              required={autocompleteEnabled}
              placeholder={cityPlaceholder}
              id="city"
              name="city"
              value={values.city}
              onChange={handleChange}
              onBlur={onBlurEvent}
              autoComplete="off"
              error={isFieldError('city', touched, errors)}
              errorMessage={errors.city}
              disabled={fieldsLocked}
              aria-disabled={fieldsLocked}
            />

            <DropdownNativeSelect
              app="websheet"
              label={stateProvinceRegionLabel}
              required={autocompleteEnabled}
              id="state_province_region"
              name="state_province_region"
              value={values.state_province_region}
              onChange={handleChange}
              options={stateProvinceRegionList}
              onBlur={onBlurEvent}
              placeholder={stateProvinceRegionPlaceholder}
              error={isFieldError('state_province_region', touched, errors)}
              errorMessage={errors.state_province_region}
              disabled={fieldsLocked}
              aria-disabled={fieldsLocked}
            />

            <InputField
              app="websheet"
              type="text"
              label={zipPostalCodeLabel}
              required={autocompleteEnabled}
              placeholder={zipPlaceholder}
              id="zip_postal_code"
              name="zip_postal_code"
              value={values.zip_postal_code}
              onChange={handleChange}
              onBlur={onBlurEvent}
              autoComplete="off"
              error={isFieldError('zip_postal_code', touched, errors)}
              errorMessage={errors.zip_postal_code}
              disabled={fieldsLocked}
              aria-disabled={fieldsLocked}
            />

            <Button
              type="submit"
              disabled={
                fieldsLocked || (autocompleteEnabled && !formik.isValid)
              }
              aria-disabled={
                fieldsLocked || (autocompleteEnabled && !formik.isValid)
              }
              aria-busy={isSubmitting}
              data-dd-action-name={actions?.address_submit_button_clicked}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Form>
      </Stack>
    </div>
  );
};
